import { useState, useEffect, useCallback } from 'react'
import { useAuth } from '../contexts/AuthContext/AuthContext'
import { auth } from '../firebase/config/config'

export interface AccountLockInfo {
  isLocked: boolean
  lockedAt?: Date
  lockedBy?: string
  lockReason?:
    | 'terms_violation'
    | 'payment_fraud'
    | 'spam'
    | 'abuse'
    | 'manual'
    | 'other'
  lockMessage?: string
  unlockedAt?: Date
  unlockedBy?: string
}

export interface AccountLockStatus {
  isLocked: boolean
  lockInfo?: AccountLockInfo
  contactEmail: string
}

export const useAccountLock = () => {
  const { user } = useAuth()
  const [lockStatus, setLockStatus] = useState<AccountLockStatus | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const checkAccountLockStatus = useCallback(async () => {
    if (!user) {
      setLockStatus(null)
      return
    }

    setLoading(true)
    setError(null)

    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        throw new Error('No authentication token')
      }

      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001/api'
      const response = await fetch(`${apiUrl}/account/lock-status`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (!response.ok) {
        const errorData = await response.json()
        throw new Error(
          errorData.error || 'Failed to check account lock status',
        )
      }

      const data: { success: boolean; data: AccountLockStatus } =
        await response.json()

      if (data.success) {
        // Convert date strings back to Date objects if lockInfo exists
        const statusWithDates = {
          ...data.data,
          lockInfo: data.data.lockInfo
            ? {
                ...data.data.lockInfo,
                lockedAt: data.data.lockInfo.lockedAt
                  ? new Date(data.data.lockInfo.lockedAt)
                  : undefined,
                unlockedAt: data.data.lockInfo.unlockedAt
                  ? new Date(data.data.lockInfo.unlockedAt)
                  : undefined,
              }
            : undefined,
        }

        setLockStatus(statusWithDates)
      } else {
        throw new Error('Failed to check account lock status')
      }
    } catch (error) {
      console.error('Error checking account lock status:', error)
      setError(
        error instanceof Error ? error.message : 'Unknown error occurred',
      )
      setLockStatus(null)
    } finally {
      setLoading(false)
    }
  }, [user])

  const getLockReasonDisplay = (reason?: string): string => {
    switch (reason) {
      case 'terms_violation':
        return 'Terms of Service Violation'
      case 'payment_fraud':
        return 'Suspicious Payment Activity'
      case 'spam':
        return 'Spam or Unwanted Behavior'
      case 'abuse':
        return 'Abusive Behavior'
      case 'manual':
        return 'Manual Review'
      case 'other':
      default:
        return 'Policy Violation'
    }
  }

  const getLockReasonColor = (reason?: string): string => {
    switch (reason) {
      case 'terms_violation':
      case 'abuse':
        return '#dc3545' // Red
      case 'payment_fraud':
        return '#fd7e14' // Orange
      case 'spam':
        return '#ffc107' // Yellow
      case 'manual':
        return '#6f42c1' // Purple
      case 'other':
      default:
        return '#6c757d' // Gray
    }
  }

  useEffect(() => {
    checkAccountLockStatus()
  }, [user, checkAccountLockStatus])

  return {
    lockStatus,
    loading,
    error,
    refetch: checkAccountLockStatus,
    isAccountLocked: lockStatus?.isLocked === true,
    lockMessage: lockStatus?.lockInfo?.lockMessage,
    lockReason: lockStatus?.lockInfo?.lockReason,
    contactEmail: lockStatus?.contactEmail || 'accounts@quickcasa.ai',
    getLockReasonDisplay,
    getLockReasonColor,
  }
}
